import { Action, SubjectRoles } from "@/api-schema-enums";
import { AppAbility } from "@/casl-provider";
import { hasDevStageDeveloperAccess } from "@/common/helpers/has-dev-stage-developer-access";
import { AttributeAppRolePrefix, extractAttributeAppRolesByPrefix } from "@/common/helpers/user-attribute-app-roles";
import { User } from "@/store/user-store";
import { AbilityBuilder } from "@casl/ability";

export function setCustomerPermissions(user: User, builder: AbilityBuilder<AppAbility>) {
  const { can } = builder;

  if (user.roles.includes(SubjectRoles.MasterdataAdministrator)) {
    can(Action.Manage, "Customer");
    return;
  }

  if (user.roles.includes(SubjectRoles.Int) || user.roles.includes(SubjectRoles.AceUser)) {
    can(Action.Read, "Customer");
  }
}

/**
 * Masterdata administrators and dev-stage developers see every customer. Everyone else is scoped by the API to the
 * company groups granted via `CG.*` attribute app roles.
 */
export function hasUnrestrictedCustomerAccess(roles: readonly SubjectRoles[] | undefined): boolean {
  return hasDevStageDeveloperAccess(roles) || Boolean(roles?.includes(SubjectRoles.MasterdataAdministrator));
}

/**
 * Company group technical ids (e.g. `CG.ABC12`) the user holds as attribute app roles.
 */
export function getUserCompanyGroupTechnicalIds(roles: readonly SubjectRoles[] | undefined): string[] {
  return extractAttributeAppRolesByPrefix([...(roles ?? [])], AttributeAppRolePrefix.CompanyGroup);
}

/**
 * Whether the API can return any customer for this user at all. Users without unrestricted access and without a
 * single `CG.*` role always receive an empty result; the UI shows a dedicated fallback instead of an empty table.
 */
export function hasCustomerCompanyGroupScope(roles: readonly SubjectRoles[] | undefined): boolean {
  return hasUnrestrictedCustomerAccess(roles) || getUserCompanyGroupTechnicalIds(roles).length > 0;
}
